/**
 * Compaction as both runtimes run it: a turn whose text is `/compact` compacts
 * the conversation instead of prompting, and a model's context window is what
 * OpenCode compacts to stay under. It imports nothing, so the cloud's server
 * and the page's client for a session on the person's own computer share one
 * rule.
 */

/** What a turn says to compact the conversation, as the composer's `/` menu sends it. */
export const COMPACT_COMMAND = '/compact';

/** No list, or an empty one: a saved turn keeps its skills and files as JSON. */
function isEmptyList(value: unknown): boolean {
  return !Array.isArray(value) || value.length === 0;
}

/**
 * Whether a turn compacts the conversation instead of prompting: its whole text is `/compact`,
 * with no files or skills (T3 Code's rule). The text matches exactly, as the composer matches a
 * draft to its `/` commands, so a draft the composer sends as a message never compacts.
 */
export function isCompactPrompt(prompt: {
  text: string;
  skills?: unknown;
  attachments?: unknown;
}): boolean {
  return (
    prompt.text === COMPACT_COMMAND && isEmptyList(prompt.skills) && isEmptyList(prompt.attachments)
  );
}

/**
 * A model's context window as OpenCode sizes a request: its input limit, else its context limit.
 * Null when neither is known, which OpenCode reports as 0, and for a model the list lacks.
 */
export function modelContextWindow(
  models: readonly { providerID: string; id: string; limit: { context: number; input?: number } }[],
  model: { providerID: string; id: string },
): number | null {
  const listed = models.find(
    (candidate) => candidate.providerID === model.providerID && candidate.id === model.id,
  );
  const window = listed === undefined ? 0 : listed.limit.input || listed.limit.context;
  return window > 0 ? window : null;
}
